import { ImagePlus, Loader2, Pencil, Trash2 } from "lucide-react";

import ConfirmationDialog from "@/components/shared/confirmDialog";

// Re-usable image management primitive: preview + upload/change + delete-with-confirm.
// Hoisted to a shared module on purpose: when a component is declared inside
// another component (especially one with cascading state like a category form),
// every parent render produces a new component reference and React unmounts/
// remounts the subtree. For a hidden file input mid-flow (user just clicked →
// native dialog opened), the remount destroys the input element before its
// onChange can fire — clicks appear to "do nothing". See B28 in
// project_bug_class_catalog memory note.
//
// Contract:
//   - All business logic (upload, delete, busy tracking) is owned by the parent
//     and passed in as handlers / state.
//   - `t` is injected so the primitive stays dependency-free at module scope.
//   - `levelLabel` is used for both alt-text and aria-label so screen readers
//     can distinguish multiple ImageBlock instances on the same page.
//   - Delete is always wrapped in ConfirmationDialog (Hayır / Evet).
const ImageBlock = ({
  url,
  busyFlag,
  inputRef,
  onUpload,
  onDelete,
  hint,
  levelLabel,
  t,
}) => (
  <div className="flex flex-col gap-2">
    {hint ? <p className="text-xs text-gray-500">{hint}</p> : null}
    <div className="flex items-center gap-3">
      {url ? (
        <img
          src={url}
          alt={
            levelLabel
              ? t("productsPage.imageAlt", "{{level}} görseli", {
                  level: levelLabel,
                })
              : ""
          }
          className="h-20 w-20 flex-shrink-0 rounded-md border object-cover"
        />
      ) : (
        <div className="flex h-20 w-20 flex-shrink-0 items-center justify-center rounded-md border border-dashed bg-gray-50 text-gray-400">
          <ImagePlus className="h-6 w-6" />
        </div>
      )}
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={busyFlag}
          aria-label={
            levelLabel
              ? url
                ? t(
                    "productsPage.changeImageFor",
                    "{{level}} görselini değiştir",
                    { level: levelLabel },
                  )
                : t(
                    "productsPage.uploadImageFor",
                    "{{level}} görseli yükle",
                    { level: levelLabel },
                  )
              : undefined
          }
          className="inline-flex h-11 items-center gap-1.5 rounded-md border bg-white px-4 text-sm font-medium hover:bg-gray-50 disabled:opacity-60"
        >
          {busyFlag ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : url ? (
            <Pencil className="h-4 w-4" />
          ) : (
            <ImagePlus className="h-4 w-4" />
          )}
          {url
            ? t("productsPage.changeImage", "Görseli değiştir")
            : t("productsPage.uploadImage", "Görsel yükle")}
        </button>
        {url && !busyFlag ? (
          <ConfirmationDialog
            title={t(
              "productsPage.deleteImageConfirmTitle",
              "Görseli silmek istiyor musunuz?",
            )}
            description={t(
              "productsPage.deleteImageConfirmDescription",
              "Bu işlem geri alınamaz. Devam etmek istediğinize emin misiniz?",
            )}
            cancelText={t("common.no", "Hayır")}
            confirmText={t("common.yes", "Evet")}
            onConfirm={onDelete}
          >
            <button
              type="button"
              aria-label={
                levelLabel
                  ? t(
                      "productsPage.deleteImageFor",
                      "{{level}} görselini sil",
                      { level: levelLabel },
                    )
                  : undefined
              }
              className="inline-flex h-11 items-center gap-1.5 rounded-md border border-red-300 bg-white px-4 text-sm font-medium text-red-600 hover:bg-red-50"
            >
              <Trash2 className="h-4 w-4" />
              {t("common.delete", "Sil")}
            </button>
          </ConfirmationDialog>
        ) : null}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={onUpload}
      />
    </div>
  </div>
);

export default ImageBlock;
